import React from 'react';
import _ from 'lodash';



const __defaultRowsPerPage = 20;



export interface IGqlPageInfo {
  nextPage?: string | null;
  pages: string[];
  previousPage?: string | null;
  totalRows: number;
}

export interface IPages {
  previous: string;
  next: string;
  first: string;
  last: string;
}

export interface IPageInfo {
  totalRows: number;
  currentPage: number;
  rowsPerPage: number;
  pages: IPages;
  pageList: string[];
}

export type FnQueryPage = (pageCursor?: string) => void;
export type FnWrappedQueryPage = (page?: keyof IPages | number) => void;

export function usePaginatedQuery({
  fnQueryPage, gqlPageInfo, wasCalled, disableInitQuery, rowsPerPage
}: {
  fnQueryPage: FnQueryPage;
  gqlPageInfo: IGqlPageInfo | undefined;
  wasCalled: boolean;
  disableInitQuery?: boolean;
  rowsPerPage?: number;
}) {
  const actualRowsPerPage = rowsPerPage ?? __defaultRowsPerPage;

  const [pageInfo, setPageInfo] = React.useState<IPageInfo>({
    totalRows: 0,
    currentPage: 0,
    rowsPerPage: 0,
    pages: {
      previous: '',
      next: '',
      first: '',
      last: ''
    },
    pageList: []
  });



  const fnWrappedQueryPage = React.useCallback<FnWrappedQueryPage>(
    async page =>
      _.isNumber(page) ?
        fnQueryPage(pageInfo.pageList[page]) :
        fnQueryPage(page ? pageInfo.pages[page] : undefined),
    [
      fnQueryPage,
      pageInfo
    ]
  );



  React.useEffect(
    () => {
      if (!disableInitQuery && !wasCalled) {
        fnWrappedQueryPage();
      }
    },
    [
      fnWrappedQueryPage,
      wasCalled,
      disableInitQuery
    ]
  );



  React.useEffect(
    () => {
      const pageCursor2Index = gqlPageInfo ?
        _.mapValues(_.invert(gqlPageInfo.pages as string[]), value => _.toNumber(value)) :
        {};

      const previousPageIndex = pageCursor2Index[gqlPageInfo?.previousPage ?? ''];
      const nextPageIndex = pageCursor2Index[gqlPageInfo?.nextPage ?? ''];
      const currentPageIndex = !_.isNil(previousPageIndex) ?
        previousPageIndex + 1 :
        (nextPageIndex ?? 1) - 1;

      setPageInfo(gqlPageInfo ? {
        totalRows: gqlPageInfo.totalRows,
        currentPage: currentPageIndex,
        rowsPerPage: actualRowsPerPage,
        pages: {
          previous: gqlPageInfo.previousPage ?? '',
          next: gqlPageInfo.nextPage ?? '',
          first: gqlPageInfo.pages[0] ?? '',
          last: _.last(gqlPageInfo.pages) ?? ''
        },
        pageList: gqlPageInfo.pages
      } : {
        totalRows: 0,
        currentPage: 0,
        rowsPerPage: actualRowsPerPage,
        pages: {
          previous: '',
          next: '',
          first: '',
          last: ''
        },
        pageList: []
      });
    },
    [
      gqlPageInfo,
      actualRowsPerPage
    ]
  );



  return [
    fnWrappedQueryPage,
    pageInfo
  ] as const;
}
